import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  Replay as RecreateIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import { MiscellaneousServices as MiscellaneousServicesIcon } from '@mui/icons-material';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useClipboard } from '../../../shared/hooks/useClipboard';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { usePipelineCredentials } from '../hooks/usePipelineCredentials';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import AllPipelineCredentialsView from './AllPipelineCredentialsView';
import { useServiceOfferingsService } from '../../service-offerings/services/serviceOfferingsService';
import type { PipelineCredentialResponse } from '../services/catalogService';
import type { ServiceOffering } from '../../../types/backend';

interface PipelineAccessTabContentProps {
  isOpen: boolean;
  onCountChange?: (count: number) => void;
}

const PipelineAccessTabContent: React.FC<PipelineAccessTabContentProps> = ({ isOpen, onCountChange }) => {
  const offeringsService = useServiceOfferingsService();
  const [services, setServices] = useState<ServiceOffering[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceOffering | null>(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLabel, setCreateLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdCredential, setCreatedCredential] = useState<PipelineCredentialResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PipelineCredentialResponse | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [mode, setMode] = useState<'byOffering' | 'all'>('byOffering');
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { copy, isCopied } = useClipboard();

  const { credentials, loading, error, dismissError, createCredential, revokeCredential } =
    usePipelineCredentials(selectedService?.id ?? null);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    const loadServices = async () => {
      setLoadingServices(true);
      try {
        const result = await offeringsService.listOfferings();
        if (mounted) setServices(result.service_offerings);
      } catch {
        // non-critical
      } finally {
        if (mounted) setLoadingServices(false);
      }
    };
    loadServices();
    return () => { mounted = false; };
  }, [isOpen, offeringsService]);

  useEffect(() => {
    if (mode === 'byOffering') onCountChange?.(credentials.length);
  }, [credentials.length, onCountChange, mode]);

  const handleCreate = useCallback(async () => {
    if (!createLabel.trim()) return;
    setCreating(true);
    try {
      const credential = await createCredential(createLabel.trim());
      setCreatedCredential(credential);
      setCreateLabel('');
      showSnackbar('Credential created');
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to create credential'), 'error');
    } finally {
      setCreating(false);
    }
  }, [createLabel, createCredential, showSnackbar]);

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    try {
      await revokeCredential(revokeTarget.client_id);
      setRevokeTarget(null);
      showSnackbar('Credential revoked');
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to revoke credential'), 'error');
    }
  }, [revokeTarget, revokeCredential, showSnackbar]);

  const handleRecreate = useCallback(async (cred: PipelineCredentialResponse) => {
    setCreateLabel(cred.label);
    setCreateOpen(true);
  }, []);

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setCreateLabel('');
    setCreatedCredential(null);
  };

  const filteredCredentials = showRevoked
    ? credentials
    : credentials.filter(c => c.status === 'active');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="byOffering">By offering</ToggleButton>
          <ToggleButton value="all">All credentials</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {mode === 'all' ? (
        <AllPipelineCredentialsView onCountChange={onCountChange} />
      ) : (
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel<ServiceOffering>
          entities={services}
          selectedEntity={selectedService}
          isNew={false}
          saving={false}
          config={{
            getPrimaryText: (s) => s.display_name || s.name,
            getSecondaryText: (s) => s.owner || 'No owner',
            getId: (s) => s.id,
            Icon: MiscellaneousServicesIcon,
          }}
          emptyStateText="No offerings found"
          emptyStateHelperText="Create one in the Offerings tab first"
          onSelect={(s) => setSelectedService(s)}
          isLoading={loadingServices}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3 }}>
        {error && (
          <InlineAlert severity="error" onClose={dismissError} sx={{ mb: 2 }}>{error}</InlineAlert>
        )}

        {!selectedService ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              Select an offering to manage pipeline credentials
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Pipeline Credentials for {selectedService.display_name || selectedService.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {credentials.some(c => c.status === 'revoked') && (
                  <FormControlLabel
                    control={<Checkbox size="small" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />}
                    label={<Typography variant="body2">Show revoked</Typography>}
                  />
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                >
                  Create Credential
                </Button>
              </Box>
            </Box>

            {filteredCredentials.length === 0 ? (
              <Typography color="text.secondary">
                {credentials.length === 0
                  ? 'No pipeline credentials for this offering. Create one to enable M2M access.'
                  : 'No active credentials. Check "Show revoked" to see revoked credentials.'}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client ID</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCredentials.map((cred) => (
                      <TableRow key={cred.client_id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {cred.client_id}
                          </Typography>
                        </TableCell>
                        <TableCell>{cred.label}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {new Date(cred.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {cred.created_by}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cred.status}
                            size="small"
                            color={cred.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {cred.status === 'active' ? (
                            <Tooltip title="Revoke credential">
                              <IconButton size="small" color="error" onClick={() => setRevokeTarget(cred)}>
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Recreate with same label">
                              <IconButton size="small" color="primary" onClick={() => handleRecreate(cred)}>
                                <RecreateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
      </Box>
      )}

      {/* Create Credential Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createdCredential ? 'Credential Created' : 'Create Pipeline Credential'}
        </DialogTitle>
        <DialogContent>
          {createdCredential ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <InlineAlert severity="warning">
                Copy these credentials now. The client secret will not be shown again.
              </InlineAlert>
              <Box>
                <Typography variant="caption" color="text.secondary">Client ID</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {createdCredential.client_id}
                  </Typography>
                  <IconButton size="small" onClick={() => copy(createdCredential.client_id)}>
                    {isCopied(createdCredential.client_id) ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
              {createdCredential.client_secret && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Client Secret</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {createdCredential.client_secret}
                    </Typography>
                    <IconButton size="small" onClick={() => copy(createdCredential.client_secret!)}>
                      {isCopied(createdCredential.client_secret!) ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              )}
              {createdCredential.token_endpoint && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Token Endpoint</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {createdCredential.token_endpoint}
                    </Typography>
                    <IconButton size="small" onClick={() => copy(createdCredential.token_endpoint!)}>
                      {isCopied(createdCredential.token_endpoint!) ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Secrets Manager ARN</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                    {createdCredential.secret_arn}
                  </Typography>
                  <IconButton size="small" onClick={() => copy(createdCredential.secret_arn)}>
                    {isCopied(createdCredential.secret_arn) ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Label"
                placeholder="e.g., github-actions-deploy, gitlab-ci"
                value={createLabel}
                onChange={(e) => setCreateLabel(e.target.value)}
                fullWidth
                size="small"
                autoFocus
                helperText="A descriptive label for this credential (e.g., pipeline or repo name)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {createdCredential ? (
            <Button onClick={handleCloseCreate} variant="contained">Done</Button>
          ) : (
            <>
              <Button onClick={handleCloseCreate}>Cancel</Button>
              <Button
                onClick={handleCreate}
                variant="contained"
                disabled={!createLabel.trim() || creating}
                startIcon={creating ? <CircularProgress size={16} /> : undefined}
              >
                Create
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation */}
      <DeleteConfirmDialog
        open={!!revokeTarget}
        title="Revoke Credential?"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        confirmText="Revoke"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to revoke the credential: <strong>{revokeTarget?.label}</strong>?
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
          {revokeTarget?.client_id}
        </Typography>
        <InlineAlert severity="warning">
          Pipelines using this credential will lose access immediately. This action cannot be undone.
        </InlineAlert>
      </DeleteConfirmDialog>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
};

export default PipelineAccessTabContent;
